import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Row,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import SalesReportService from "../../../services/OrderingServices/SalesReportService";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";

const CategoryWiseReport = () => {
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);

  // filters
  const [filterText, setFilterText] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  // Format helper
  const formatDate = (date) => date?.toLocaleDateString("en-CA");

  // Fetch data
  const fetchData = async (
    from = fromDate,
    to = toDate,
    category = filterText
  ) => {
    if (!from || !to) {
      alert("Please select both From and To dates");
      return;
    }

    setLoading(true);
    try {
      const response = await SalesReportService.categoryWise({
        categoryName: category,
        fromDate: formatDate(from),
        toDate: formatDate(to),
      });
      setAllData(response || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setAllData([]);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      sortable: false,
      width: "75px",
    },
    {
      name: "Category Name",
      selector: (row) => row.categoryName,
      sortable: true,
    },
    {
      name: "Total Quantity",
      selector: (row) => row.totalQuantity,
      sortable: true,
      right: true,
      width: "150px",
    },
    {
      name: "Total Sales",
      selector: (row) => row.totalSales,
      sortable: true,
      right: true,
      width: "150px",
      cell: (row) => <NumberWithCommas number={row.totalSales} />,
    },
  ];

  // Initialize 1-month range on mount + auto-fetch
  useEffect(() => {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1);

    setFromDate(lastMonth);
    setToDate(today);
    setDateRange([lastMonth, today]);

    fetchData(lastMonth, today, null); // fetch all categories initially
  }, []);

  // Auto-fetch when category filter changes
  useEffect(() => {
    if (fromDate && toDate) {
      fetchData(fromDate, toDate, filterText);
    }
  }, [filterText]); // only trigger on filterText change

  // totals
  const totalQuantity = allData.reduce(
    (sum, item) => sum + item.totalQuantity,
    0
  );
  const totalSales = allData.reduce((sum, item) => sum + item.totalSales, 0);

  const getDateRangeDisplay = () => {
    if (!fromDate || !toDate) return "Select Date Range";
    const start = new Date(fromDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const end = new Date(toDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${start} - ${end}`;
  };

  return (
    <>
      {/* Main Content */}
      <div className="p-3 p-lg-4 mt-1">
        {/* Filters */}
        <Row className="mb-3 g-2 align-items-center">
          {/* Title & Date Display */}
          <Col md={4}>
            {" "}
            <div>
              <h5 className="mb-1 fw-bold text-dark">Category Wise Report</h5>
              <p className="text-muted small mb-0">{getDateRangeDisplay()}</p>
            </div>
          </Col>

          {/* Filters & Buttons */}
          <Col md={8}>
            <Row className="g-2 align-items-end">
              {/* Product Name Filter */}
              <Col md={2}></Col>
              <Col md={4}>
                <InputGroup>
                  <Input
                    type="text"
                    placeholder="Filter by Category Name"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                  />
                  <InputGroupText>
                    <i className="bx bx-search"></i>
                  </InputGroupText>
                </InputGroup>
              </Col>
              <Col md={4}>
                <InputGroup>
                  <Flatpickr
                    options={{
                      mode: "range",
                      dateFormat: "Y-m-d",
                    }}
                    value={dateRange}
                    onChange={(dates) => {
                      if (dates.length === 2) {
                        setDateRange(dates);
                        setFromDate(dates[0]);
                        setToDate(dates[1]);
                      }
                    }}
                    placeholder="Select Date Range"
                    className="form-control"
                  />
                  <InputGroupText style={{ cursor: "pointer" }}>
                    <i className="bx bx-calendar"></i>
                  </InputGroupText>
                </InputGroup>
              </Col>
              {/* <Col md={3}>
            <InputGroup>
              <Flatpickr
                options={{ dateFormat: "Y-m-d" }}
                value={fromDate}
                onChange={(dates) => setFromDate(dates[0])}
                className="form-control"
                placeholder="From Date"
              />
              <InputGroupText style={{ cursor: "pointer" }}>
                <i className="bx bx-calendar"></i>
              </InputGroupText>
            </InputGroup>
          </Col> */}
              {/* <Col md={3}>
            <InputGroup>
              <Flatpickr
                options={{ dateFormat: "Y-m-d" }}
                value={toDate}
                onChange={(dates) => setToDate(dates[0])}
                className="form-control"
                placeholder="To Date"
              />
              <InputGroupText style={{ cursor: "pointer" }}>
                <i className="bx bx-calendar"></i>
              </InputGroupText>
            </InputGroup>
          </Col> */}
              <Col md={2}>
                <Button
                  color="primary"
                  className="w-100"
                  onClick={() => fetchData()}
                >
                  <i className="bx bx-search-alt me-1"></i>
                  Generate
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Table */}
        <CustomDataTable
          responsive
          striped
          fixedHeader
          highlightOnHover
          pointerOnHover
          persistTableHead
          columns={columns}
          progressPending={loading}
          customStyles={{
            headCells: {
              style: {
                fontSize: "0.92rem",
                fontWeight: 610,
              },
            },
            rows: {
              style: {
                fontSize: "0.9rem",
              },
            },
          }}
          progressComponent={
            <div className="my-3 text-center">
              <Triangle visible height="80" width="80" color="#5B71B9" />
              <h5 className="mt-1">Loading...</h5>
            </div>
          }
          data={allData}
          noDataComponent={
            <div className="text-center my-5 w-100">
              <i className="ri-emotion-unhappy-line display-4 text-secondary" />
              <h5 className="fw-semibold text-secondary">No Data Found</h5>
              <p className="text-muted mb-0">
                No category data available for the selected filters.
              </p>
            </div>
          }
        />

        {/* Summary Footer */}
        {allData.length > 0 && (
          <div
            className="p-3 mt-3"
            style={{
              background: "#f8f9fa",
              borderRadius: "0.375rem",
              border: "1px solid #dee2e6",
            }}
          >
            <Row className="fw-bold">
              <Col md={6}>
                <h6 className="mb-0 text-dark">Total Summary</h6>
              </Col>
              <Col md={3} className="text-end">
                <span className="text-muted me-2">Quantity:</span>
                <span className="text-dark">{totalQuantity}</span>
              </Col>
              <Col md={3} className="text-end">
                <span className="text-muted me-2">Sales:</span>
                <span className="text-dark">
                  <NumberWithCommas number={totalSales} />
                </span>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </>
  );
};

export default CategoryWiseReport;
